import { APP_INSTALLER_HEIGHT } from "@constants/ui";
import usePreference from "@hooks/usePreference";
import { cn } from "kz-ui-sdk";
import React, { useEffect, useState } from "react";

interface DialogFullPageProps {
  open: boolean;
  children: React.ReactNode;
  className?: string;
  fullPage?: boolean;
}

/**
 * Clean version of dialog full page.
 * Easy to use or customize if needed.
 * No outside click to close.
 * No backdrop.
 */
const DialogFullPage = ({ open, children, className = "", fullPage = true }: DialogFullPageProps) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { isDisplayAppInstaller } = usePreference();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setShouldRender(true);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      document.body.style.overflow = "";

      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Clean up body overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed inset-0 left-1/2 z-[10] flex w-screen max-w-md -translate-x-1/2 items-center justify-center bg-body bg-cover bg-no-repeat transition-opacity duration-300",
        isVisible ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
        className,
      )}
      style={{
        top: isDisplayAppInstaller ? APP_INSTALLER_HEIGHT : "0",
        height: isDisplayAppInstaller ? `calc(100vh - ${APP_INSTALLER_HEIGHT}px)` : "100vh",
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className={cn("h-full w-full")}>{children}</div>
    </div>
  );
};

export default DialogFullPage;
